import {
  deleteV1CompaniesCompanyIdM2mClientsId,
  getV1CompaniesCompanyIdM2mClients,
  patchV1CompaniesCompanyIdM2mClientsId,
  postV1CompaniesCompanyIdM2mClients,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { M2MClient, parseCreatedM2MClient } from '~hooks/useApiClients/models';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';

interface FetchApiClientsParams {
  companyId: string;
  cursor?: string;
  limit?: number;
  link?: PaginationLink;
}

interface RevokeApiClientParams {
  companyId: string;
  apiClientId: string;
}

interface CreateApiClientParams {
  companyId: string;
  name: string;
  iamRoleIds: string[];
  slug: string | null;
}

interface UpdateApiClientParams {
  companyId: string;
  apiClientId: string;
  name: string;
  slug: string | null;
}

export default function useApiClients() {
  const fetchApiClients = useCallback(
    async ({ companyId, link, cursor, limit }: FetchApiClientsParams) => {
      try {
        const response = await getV1CompaniesCompanyIdM2mClients({
          path: { 'company-id': companyId },
          query: {
            'page[limit]': limit || DEFAULT_API_PAGE_SIZE,
            ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
          },
        });
        const apiClients = response.data.data.map(M2MClient.parse);
        const pagination = extractPagination(response);

        return { data: apiClients, pagination };
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.api_clients.failed_to_fetch'),
        );
      }
    },
    [],
  );

  const revokeApiClient = useCallback(
    async ({ companyId, apiClientId }: RevokeApiClientParams) => {
      try {
        await deleteV1CompaniesCompanyIdM2mClientsId({
          path: { 'company-id': companyId, id: apiClientId },
        });
        return true;
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.api_clients.failed_to_revoke'),
        );
      }
    },
    [],
  );

  const createApiClient = useCallback(
    async ({ companyId, name, iamRoleIds, slug }: CreateApiClientParams) => {
      try {
        const response = await postV1CompaniesCompanyIdM2mClients({
          path: { 'company-id': companyId },
          body: { name, iam_role_ids: iamRoleIds, slug },
        });
        return parseCreatedM2MClient(response.data.data);
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.api_clients.failed_to_create'),
        );
      }
    },
    [],
  );

  const updateApiClient = useCallback(
    async ({ companyId, apiClientId, name, slug }: UpdateApiClientParams) => {
      try {
        const response = await patchV1CompaniesCompanyIdM2mClientsId({
          path: { 'company-id': companyId, id: apiClientId },
          body: { name, slug },
        });
        return M2MClient.parse(response.data.data);
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.api_clients.failed_to_update'),
        );
      }
    },
    [],
  );

  return { fetchApiClients, revokeApiClient, createApiClient, updateApiClient };
}
